import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import moment from 'moment';
import { CDataTable, CRow, CCol, CSidebar, CInput, CLink, CAlert } from '@coreui/react';
import { FilterByOption, FormField, ITable, FilterTypes } from '@transferz/models';
import { isObject } from '@transferz/utils';
import { Form } from '../dataForm/Form';
import { ItemsPerPage } from './ItemsPerPage';
import { TableFilter } from './TableFilter';
import { DataTableHeader } from './DataTableHeader';
import { DataTablePagination } from './DataTablePagination';
import { Button } from '../button/Button';
import { Loading } from '../loading/Loading';
import { useTranslation } from 'react-i18next';
import { NotificationType } from '@transferz/models';

function getFlatKeys(filterByOptions: FilterByOption[]) {
  const keys = filterByOptions?.map(({ key, type, args }: any) => (
    type === FilterTypes.dateRange ? args.fields : key
  ));
  return [].concat.apply([], keys);
};

function getQueryBody(queries: any, defaultParams: any, urlQueryParams: any, filterByOptions: FilterByOption[], uniqueUrlKeys: boolean = false, formId?: string) {
  const flat: string[] = getFlatKeys(filterByOptions);
  let hasFiltersFromOtherTable = false;
  const ACCEPTABLE_QUERY_PARAMS = [
    ...flat,
    'page',
    'size',
    'sort',
  ];
  const queryBody: any = {
    ...queries,
  };
  if (defaultParams) {
    Object.keys(defaultParams).forEach((defaultParam: any) => {
      queryBody[defaultParam] = defaultParams[defaultParam];
    });
  }
  for (const [key, value] of urlQueryParams) {
    const formKey = uniqueUrlKeys ? key.replace(formId, '') : key;
    console.log(formKey);
    if (ACCEPTABLE_QUERY_PARAMS.includes(formKey)) {
      queryBody[formKey] = JSON.parse(value);
    } else {
      hasFiltersFromOtherTable = true;
    }
  }
  return { queryBody, hasFiltersFromOtherTable };
};

function constructFiltersFormFields(filterByOptions: FilterByOption[], params: any, defaultParams: any, includeFormIdInURL: boolean, formId: string): FormField[] {
  const defaultProps = {
    layout: 'direction-col',
    displaySize: '12',
  };
  const fields = filterByOptions.map((option: FilterByOption): any => {
    const pureOption = { ...option };
    if (pureOption.args) {
      delete pureOption.args;
    }
    if (pureOption.type === FilterTypes.text || pureOption.type === FilterTypes.number || pureOption.type === FilterTypes.dynamicSearch) {
      return {
        ...defaultProps,
        ...pureOption,
      };
    } else if (pureOption.type === FilterTypes.dateRange) {
      return {
        ...defaultProps,
        ...pureOption,
        orientation: option?.args?.orientation,
      };
    } else if (pureOption.type === FilterTypes.select) {
      return {
        ...defaultProps,
        ...pureOption,
        values: option?.args?.options,
        isMulti: option?.args?.isMulti
      };
    } else if (pureOption.type === FilterTypes.autocomplete) {
      return {
        ...defaultProps,
        ...pureOption,
        values: option?.args?.options,
        isMulti: option?.args?.isMulti,
        defaultValue: option?.args?.defaultValue,
        onChange: option?.args?.onChange,
        defaultOptions: option?.args?.defaultOptions,
        placeholder: option?.args?.placeholder,
      }
    }
  });
  const finalFields = assignDefaultValuesToFormFields(fields, filterByOptions, params, defaultParams, includeFormIdInURL, formId);
  return finalFields;
};

function assignDefaultValuesToFormFields(fields: FormField[], filterByOptions: FilterByOption[], params: any, defaultParams: any, includeFormIdInURL: boolean, formId: string) {
  fields.forEach((field: FormField) => {
    const fieldKey = includeFormIdInURL ? `${formId}${field.key}` : field.key;
    const option: any = filterByOptions.find((option: FilterByOption) => option.key === (field as FormField).key);
    if (option.args?.transformField) {
      const item = params.get(fieldKey);
      if (item) {
        const parsedItem = JSON.parse(item);
        if (defaultParams?.[fieldKey]) {
          if (Array.isArray(defaultParams?.[fieldKey])) {
            parsedItem.concat(defaultParams?.[fieldKey]);
          }
        }
        field.defaultValue = option.args?.transformField(parsedItem);
      } else if (defaultParams?.[fieldKey]) {
        field.defaultValue = option.args?.transformField(defaultParams[fieldKey]);
      }
    } else {
      if (field.type === FilterTypes.select) {
        const selectOptions = params.get(fieldKey);
        if (selectOptions) {
          const parsedOptions = JSON.parse(selectOptions);
          if (Array.isArray(parsedOptions)) {
            field.defaultValue = parsedOptions.map((option: string) => {
              const item = (field.values as any[]).find(({ value }: any) => value === option);
              return {
                label: item?.label,
                value: item?.value,
              };
            });
          } else {
            const item = (field.values as any[]).find(({ value }: any) => value === parsedOptions);
            field.defaultValue = {
              label: item?.label,
              value: item?.value,
            };
          }
        } else if (defaultParams?.[fieldKey]) {
          field.defaultValue = defaultParams[fieldKey].map((option: string) => {
            const item = (field.values as any[]).find(({ value }: any) => value === option);
            return {
              label: item?.label,
              value: item?.value,
            };
          });
        }
      } else if (field.type === FilterTypes.autocomplete) {
        const selectOptions = params.get(fieldKey);
        if (selectOptions) {
          const parsedOptions = JSON.parse(selectOptions);
          if (field.values && typeof field.values === 'function') {
            const promiseToEval: Promise<Array<any>> = new Promise((callback: Function) => { });
            (field.values('', field, promiseToEval) as any).then((res: Array<any>) => {
              field.defaultValue = res[0];
            });
          } else {
            field.defaultValue = parsedOptions;
          }
        }
      } else if (field.type === FilterTypes.dateRange) {
        const option = filterByOptions.find((option: FilterByOption) => option.key === (field as FormField).key);
        const startDate = params.get(includeFormIdInURL ? `${formId}${option?.args?.fields[0]}` : option?.args?.fields[0]);
        const endDate = params.get(includeFormIdInURL ? `${formId}${option?.args?.fields[1]}` : option?.args?.fields[1]);
        field.defaultValue = {
          startDate: startDate ? moment(JSON.parse(startDate)) : null,
          endDate: endDate ? moment(JSON.parse(endDate)) : null,
        }
      } else if (field.type === FilterTypes.text || field.type === FilterTypes.number || field.type === FilterTypes.dynamicSearch) {
        const value = params.get(fieldKey);
        const parsedValue = JSON.parse(value);
        field.defaultValue = parsedValue;
      }
    }
  });
  return fields;
};

export const TableV2: React.FC<ITable & {includeFormIdInURL?: boolean}> = ({
  query,
  queryVariablesKey,
  selector,
  fields,
  noItemsViewText = 'No items',
  showFilters = true,
  showAllFilters = false,
  showPagination = true,
  showItemsPerPage = true,
  scopedSlots,
  filterByOptions,
  defaultParams,
  transformQueries,
  onDataFetched,
  classes,
  responseVariableKey = 'results',
  searchInformation,
  actionBtn,
  formId = 'allFiltersForm',
  filterOnlyDay = false,
  showNotification = false,
  includeFormIdInURL = false,
}: any) => {
  const history = useHistory();
  const urlQueryParams = new URLSearchParams(useLocation().search);

  const toggle = filterByOptions?.find((option: any) => option.type === 'TOGGLE');
  const toggleParam = urlQueryParams.get(includeFormIdInURL ? `${formId}${toggle?.key}` : toggle?.key);
  const isToggleOnParam = toggleParam ? JSON.parse(toggleParam)?.length === 0 : false;

  const [filtersFormFields, setFiltersFormFields] = useState([]) as any;
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [shouldRerender, setShouldRerender] = useState(false);
  const [queries, setQueries] = useState<any>({
    page: 0,
    size: defaultParams?.size || 10,
  });
  const [isResultsEmpty, setIsResultsEmpty] = useState(false);
  const [isPickUpDateFilter, setIsPickUpDateFilter] = useState(false);
  const { t } = useTranslation();
  const sidebarRef = useRef(null) as any;
  const needHelpSidebarRef = useRef(null) as any;
  const [showHelpSidebar, setShowHelpSidebar] = useState(false);

  const handleDocumentClick = (event: any) => {
    if (!sidebarRef.current.contains(event.target) && document.body.contains(event.target)) {
      setShowFilterBar(false);
    }
    if (!needHelpSidebarRef.current.contains(event.target) && document.body.contains(event.target)) {
      setShowHelpSidebar(false);
    }
  };

  useEffect(() => {
    if (showAllFilters) {
      const formFields = constructFiltersFormFields(filterByOptions, urlQueryParams, defaultParams, includeFormIdInURL, formId);
      setFiltersFormFields(formFields);
      document.addEventListener('click', handleDocumentClick);
      return () => {
        document.removeEventListener('click', handleDocumentClick);
      };
    }
  }, [urlQueryParams.toString()]);

  const { queryBody, hasFiltersFromOtherTable } = getQueryBody(queries, defaultParams, urlQueryParams, filterByOptions, includeFormIdInURL, formId);

  const { loading, data } = query({
    variables: {
      [queryVariablesKey]: queryBody,
    },
  });

  useEffect(() => {
    if (onDataFetched && data) {
      onDataFetched(responseVariableKey ? (data[selector]?.[responseVariableKey] || []) : (data[selector] || []));
    }

    if (searchInformation && data) {
      searchInformation({
        filter: queryBody,
        data,
      });
    }

    if (showNotification && data?.[selector]?.[responseVariableKey]?.length === 0) {
      setIsResultsEmpty(true);
    } else {
      setIsResultsEmpty(false);
    }
  }, [data]);

  const setUrlQueryParams = (item: any, type: string, removedFilters?: any) => {
    if (type === 'filter') {
      if (removedFilters) {
        removedFilters.forEach((filter: string) => {
          urlQueryParams.delete(includeFormIdInURL ? `${formId}${filter}`: filter);
        });
      }
      Object.keys(item).forEach((_item: any) => urlQueryParams.set(includeFormIdInURL ? `${formId}${_item}` : _item, JSON.stringify(item[_item])));
    } else if (type === 'pagination') {
      urlQueryParams.set(includeFormIdInURL ? `${formId}page` : 'page', JSON.stringify(item.page));
      urlQueryParams.set(includeFormIdInURL ? `${formId}size` : 'size', JSON.stringify(item.size));
    } else if (type === 'sort') {
      urlQueryParams.set(includeFormIdInURL ? `${formId}sort` : 'sort', JSON.stringify(item));
    }
    history.push({
      search: type !== 'clear' ? urlQueryParams.toString() : '',
    });
  };

  const handleFilterChange = (filter: any) => {
    setQueries({
      ...queries,
      ...filter,
    });
    setUrlQueryParams(filter, 'filter');
  };

  let filterChangeTimeout: any;
  const handleDebouncedFilterChange = (filter: any) => {
    if (filterChangeTimeout) {
      clearTimeout(filterChangeTimeout);
    }
    filterChangeTimeout = setTimeout(() => {
      handleFilterChange(filter);
    }, 500);
  };

  const handlePaginationChange = ({ page, size }: any) => {
    const pagination = { page: page - 1, size };
    setQueries({
      ...queries,
      ...pagination
    });
    setUrlQueryParams(pagination, 'pagination');
  };

  const handleSorterChange = (values: any) => {
    const sort = values.filter(({ order }: any) => !!order).map(({ by, order }: any) => `${by};${order}`);
    setQueries({
      ...queries,
      sort,
    });
    setUrlQueryParams(sort, 'sort');
  };

  const handleToggleChange = (value: boolean) => {
    const defaultValue = defaultParams?.[toggle?.key] || queryBody[toggle?.key];
    const toggleValue = value ? [] : defaultValue;
    setQueries({
      ...queries,
      [toggle?.key]: toggleValue,
    });
    setUrlQueryParams({ [toggle?.key]: toggleValue }, 'filter');
  };

  const clearAllFilters = () => {
    const clearedQueries: any = { ...queryBody };
    const flat: string[] = getFlatKeys(filterByOptions.filter((option: any) => option.type !== FilterTypes.dynamicSearch));
    flat.forEach((key: string) => {
      if (urlQueryParams.toString().indexOf(includeFormIdInURL ? `${formId}${key}` : key) !== -1) {
        urlQueryParams.delete(includeFormIdInURL ? `${formId}${key}` : key);
      }
      delete clearedQueries[key];
    });
    setQueries({
      ...clearedQueries,
    });
    history.push({
      search: urlQueryParams.toString(),
    });
    setShouldRerender(true);
    setIsPickUpDateFilter(false);
    setTimeout(() => setShouldRerender(false));
  };

  function monthsBackFrom(date: Date, n: number): Date {
    const result = new Date(date);
    result.setMonth(result.getMonth() - n);
    return result;
  }

  const handleNotificationMessage = () => {
    if (isPickUpDateFilter) {
      return 'No journeys were found that match your search criteria.'
    } else {
      const now = new Date();
      const twoMonthBack = moment(monthsBackFrom(now, 2)).format('DD-MM-YYYY');
      return `Your search doesn't specify a pickup date filter, so we've only searched for journeys with a pickup date after ${twoMonthBack}. If you're looking for a journey with an earlier pickup date, please adjust the pickup date filter.`;
    }
  }

  return (
    <div className={`${classes?.length ? classes.join(' ') + ' ' : ''} table-wrapper`}>
      <div className="table-content">
        {(showItemsPerPage || showFilters || showAllFilters) && (
          <CRow className="mb-2">
            {showItemsPerPage && (
              <CCol>
                <ItemsPerPage
                  itemsPerPage={queryBody.size || queries.size}
                  onChange={(size) => {
                    handlePaginationChange({ size, page: 1 });
                  }}
                />
              </CCol>
            )}
            {showFilters && filterByOptions && !showAllFilters && (
              <CCol sm="8">
                <TableFilter
                  showToggle={!!toggle}
                  toggleLabel={toggle?.label}
                  isToggleOn={isToggleOnParam || toggle?.args?.checked}
                  setIsToggleOn={handleToggleChange}
                  queries={queryBody}
                  filterByOptions={filterByOptions.filter((option: any) => option.type !== 'TOGGLE')}
                  onFilterChange={(value: any, activeFilter: string, filterType: string) => {
                    if (filterType === 'ENUM_LIST') {
                      handleFilterChange({ [activeFilter]: [value] });
                    } else if (filterType === 'DATE_RANGE') {
                      const dateRange = filterByOptions.find((option: any) => option.key === activeFilter);
                      handleFilterChange({
                        [dateRange?.args?.fields[0]]: value?.startDate,
                        [dateRange?.args?.fields[1]]: value?.endDate,
                      });
                    } else {
                      handleFilterChange({ [activeFilter]: value });
                    }
                  }}
                />
              </CCol>
            )}
            {showAllFilters && (
              <CCol sm="8" className="d-flex justify-content-end align-items-start">
                {filtersFormFields.find((option: any) => option.type === FilterTypes.dynamicSearch) && (
                  <div className="mr-2 d-flex flex-column">
                    <CInput
                      type="text"
                      label="Dynamic Search"
                      className="mb-1"
                      placeholder={t('journey.searchDriverCode')}
                      onChange={(e: any) => handleDebouncedFilterChange({ "dynamicSearch": e.target.value })}
                    />
                    <CLink onClick={() => setTimeout(() => setShowHelpSidebar(true))}>{t('journey.helpFindingJourney')}</CLink>
                  </div>
                )}
                { actionBtn }
                <Button
                  title="Show all filters"
                  color="info"
                  onClick={() => setTimeout(() => setShowFilterBar(true))}
                />
              </CCol>
            )}
          </CRow>
        )}
        {(data && showNotification && data?.[selector]?.[responseVariableKey]?.length === 0)
          ? <CAlert color={isPickUpDateFilter ? NotificationType.Light : NotificationType.Warning}>
            <span>
              {handleNotificationMessage()}
            </span>
          </CAlert> : null}
        {!isResultsEmpty ?<CDataTable
          items={responseVariableKey ? (data?.[selector]?.[responseVariableKey] || []) : (data?.[selector] || [])}
          fields={fields}
          header={false}
          striped={true}
          hover={true}
          theadTopSlot={(
            <DataTableHeader
              fields={fields}
              sorterValues={[]}
              onSorterChange={handleSorterChange}
            />
          )}
          underTableSlot={showPagination && (
            <DataTablePagination
              page={queries.page + 1}
              totalEntries={data?.[selector]?.pagination?.totalResults}
              itemsPerPage={queries.size}
              onChange={(page) => {
                handlePaginationChange({ size: queries.size, page });
              }}
            />
          )}
          loading={loading}
          noItemsView={{ noResults: noItemsViewText, noItems: noItemsViewText }}
          noItemsViewSlot={loading && (!data || !data.length) ? <div className="emptyRows" /> : undefined}
          scopedSlots={scopedSlots}
        /> : null }
      </div>
      {showAllFilters && (
        <div ref={sidebarRef}>
          <CSidebar
            aside={true}
            show={showFilterBar}
            size="lg"
            colorScheme="light"
            onShowChange={() => setShowFilterBar(false)}
            className="filter-bar"
          >
            <div className="filter-header">
              <span></span>
              <h3>Filtering</h3>
              <span onClick={() => setShowFilterBar(false)}>Close</span>
            </div>
            <div className="filter-actions">
              <Button
                title="Apply filters"
                color="success"
                type="submit"
                form={formId}
              />
              <Button
                title="Remove all filters"
                color="danger"
                onClick={clearAllFilters}
              />
            </div>
            {shouldRerender ? (
              <Loading />
            ) : (
              <Form
                id={formId}
                filterOnlyDay={filterOnlyDay}
                data={{}}
                fields={filtersFormFields.filter((option: any) => option.type !== FilterTypes.dynamicSearch)}
                onSubmit={async (formData: any) => {
                  let submitData: any = {};
                  const shouldApplyFilters = Object.keys(formData).some((key) => formData[key]);
                  if (shouldApplyFilters) {
                    if (showNotification && isObject(formData?.pickupDate) && (typeof formData?.pickupDate?.startDate === 'string' || typeof formData?.pickupDate?.endDate === 'string')) {
                      setIsPickUpDateFilter(true);
                    }
                    Object.keys(formData).forEach((key: string) => {
                      if (isObject(formData[key]) && formData[key].value && (typeof formData[key].value === 'string' || typeof formData[key].value === 'number')) {
                        submitData[key] = formData[key].value;
                      } else if (isObject(formData[key]) && formData[key]?.startDate || formData[key]?.endDate) {
                        const option = filterByOptions.find((option: any) => option.key === key);
                        if (option?.args?.fields?.length) {
                          if (formData[key]?.startDate) {
                            submitData[option.args.fields[0]] = formData[key].startDate;
                          }
                          if (formData[key]?.endDate) {
                            submitData[option.args.fields[1]] = formData[key].endDate;
                          }
                        }
                      } else if (Array.isArray(formData[key])) {
                        formData[key].forEach((item: any) => {
                          if (isObject(item) && item.value) {
                            submitData[key] = submitData[key] || [];
                            submitData[key].push(item.value);
                          }
                        });
                      } else if (formData[key]) {
                        submitData[key] = formData[key];
                      }
                    });
                    if ((typeof transformQueries === 'function')) {
                      submitData = transformQueries(submitData);
                    }
                    const clearedQueries: any = { ...queryBody, ...submitData };
                    const removedFilters: any = [];
                    Object.keys(queryBody).forEach((key: string) => {
                      if (formData.hasOwnProperty(key) && !formData[key]) {
                        removedFilters.push(key);
                        delete clearedQueries[key];
                      }
                    });
                    setQueries({
                      ...clearedQueries,
                    });
                    setUrlQueryParams(submitData, 'filter', removedFilters);
                  } else {
                    const clearedQueries: any = { ...queryBody };
                    Object.keys(queryBody).forEach((key: string) => {
                      if (formData.hasOwnProperty(key) && !defaultParams.hasOwnProperty(key)) {
                        delete clearedQueries[key];
                      }
                    });
                    setQueries({
                      ...clearedQueries,
                    });
                    if (!hasFiltersFromOtherTable) {
                      setUrlQueryParams({}, 'clear');
                    }
                    setIsPickUpDateFilter(false);
                  }
                  setTimeout(() => setShowFilterBar(false));
                }}
              />
            )}
          </CSidebar>
        </div>
      )}
      <div ref={needHelpSidebarRef}>
        <CSidebar
          aside={true}
          show={showHelpSidebar}
          size="lg"
          colorScheme="light"
          onShowChange={() => setShowHelpSidebar(false)}
          className="filter-bar"
        >
          <div className="d-flex justify-content-end filter-header">
            <span onClick={() => setTimeout(() => setShowHelpSidebar(false))}>Close</span>
          </div>
          <div className="d-flex justify-content-center mt-5">
            <h3 className="p-3">{t('journey.gladToHelp')}</h3>
          </div>
          <ul>
            <li>{t('booking.bookingCode')}</li>
            <li>{t('journey.journeyCode')}</li>
            <li>{t('journey.journeyId')}</li>
            <li>{t('journey.executionCode')}</li>
            <li>{t('booking.reference')}</li>
            <li>{t('booking.booker.companyName')}</li>
            <li>{t('booking.booker.reference')}</li>
            <li>{t('booking.partnerReference')}</li>
            <li>{t('journey.travellerEmailAddress')}</li>
            <li>{t('journey.travellerName')}</li>
            <li>{t('journey.travellerPhone')}</li>
          </ul>
        </CSidebar>
      </div>
    </div>
  );
};
